import * as Schema from "effect/Schema";
import { searchableSetting } from "./settingsSearch";
import { useCallback, type ReactNode } from "react";

import {
  DEFAULT_UNIFIED_SETTINGS,
  MAX_APP_FONT_SIZE,
  MAX_PROMPT_FONT_SIZE,
  MAX_TERMINAL_FONT_SIZE,
  MIN_APP_FONT_SIZE,
  MIN_PROMPT_FONT_SIZE,
  MIN_TERMINAL_FONT_SIZE,
} from "@cmmd-forge/contracts/settings";

import { useLocalStorage } from "~/hooks/useLocalStorage";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "~/lib/touchTarget";
import { useSettings, useUpdateSettings } from "~/hooks/useSettings";
import { cn } from "~/lib/utils";

import { SettingResetButton, SettingsRow, SettingsSection } from "./settingsLayout";
import { Switch } from "../ui/switch";

/**
 * Typography.
 *
 * Two views over the same settings. **Simple** offers one sans and one
 * monospace, each cascading to every surface it reaches — which is what almost
 * everyone wants and is why it is the default. **Advanced** splits out the
 * prompt and the terminal, which are the two surfaces with a real reason to
 * differ: the composer is prose you write, and a terminal is a character grid
 * whose size decides how many columns fit.
 *
 * Every row carries a live preview rendered in the actual choice, because the
 * question a font picker answers is "what will this feel like to read", and a
 * dropdown showing the family *name* cannot answer it.
 */

/** Local, not synced: which view you last had open is a habit, not a preference. */
const TYPOGRAPHY_ADVANCED_STORAGE_KEY = "forge:typography-advanced";

const SANS_CATALOG = [
  "",
  "SF Pro",
  "Inter",
  "Helvetica Neue",
  "Segoe UI",
  "Roboto",
  "Georgia",
] as const;

const MONO_CATALOG = [
  "",
  "SF Mono",
  "Menlo",
  "JetBrains Mono",
  "Fira Code",
  "Consolas",
  "Courier New",
] as const;

function FamilySelect(props: {
  readonly label: string;
  readonly value: string;
  readonly catalog: readonly string[];
  readonly onChange: (value: string) => void;
}): ReactNode {
  return (
    <select
      aria-label={props.label}
      className={cn(
        // `min-h-touch` not `h-11`: the numeric scale is rem-based against a
        // user-adjustable root, so it cannot express a platform minimum at
        // every setting. Desktop density returns from `md`.
        "min-h-touch w-full min-w-0 flex-1 rounded-[var(--control-radius)] border border-input bg-background px-2 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring",
        NO_ZOOM_FIELD_TEXT_CLASS,
        "md:h-8 md:min-h-0 md:w-40 md:flex-none md:text-xs",
      )}
      onChange={(event) => {
        props.onChange(event.currentTarget.value);
      }}
      value={props.value}
    >
      {props.catalog.map((family) => (
        <option key={family || "default"} value={family}>
          {family === "" ? "Default" : family}
        </option>
      ))}
    </select>
  );
}

function SizeSelect(props: {
  readonly label: string;
  readonly value: number;
  readonly minimum: number;
  readonly maximum: number;
  readonly onChange: (value: number) => void;
}): ReactNode {
  const sizes: number[] = [];
  for (let size = props.minimum; size <= props.maximum; size += 1) sizes.push(size);
  return (
    <select
      aria-label={props.label}
      className={cn(
        "min-h-touch w-24 shrink-0 rounded-[var(--control-radius)] border border-input bg-background px-2 text-foreground tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring",
        NO_ZOOM_FIELD_TEXT_CLASS,
        "md:h-8 md:min-h-0 md:w-20 md:text-xs",
      )}
      onChange={(event) => {
        props.onChange(Number(event.currentTarget.value));
      }}
      value={props.value}
    >
      {sizes.map((size) => (
        <option key={size} value={size}>
          {size} px
        </option>
      ))}
    </select>
  );
}

/** Family picker + size picker, the pair every typography row uses. */
function FontControls(props: {
  readonly familyLabel: string;
  readonly family: string;
  readonly catalog: readonly string[];
  readonly onFamilyChange: (value: string) => void;
  readonly sizeLabel: string;
  readonly size: number;
  readonly minimum: number;
  readonly maximum: number;
  readonly onSizeChange: (value: number) => void;
}): ReactNode {
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center md:w-auto">
      <FamilySelect
        catalog={props.catalog}
        label={props.familyLabel}
        onChange={props.onFamilyChange}
        value={props.family}
      />
      <SizeSelect
        label={props.sizeLabel}
        maximum={props.maximum}
        minimum={props.minimum}
        onChange={props.onSizeChange}
        value={props.size}
      />
    </div>
  );
}

/**
 * A preview panel.
 *
 * Rendered with the live custom properties rather than the chosen values
 * passed down as inline styles: if the preview took its own copy it would look
 * right while the app looked wrong, which is the one failure a preview must
 * not have.
 */
function PreviewFrame(props: { readonly children: ReactNode; readonly className?: string }) {
  return (
    <div
      className={cn(
        "mt-2 overflow-hidden rounded-[var(--control-radius)] border border-border bg-card px-3 py-2.5",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
}

function InterfacePreview(): ReactNode {
  return (
    <PreviewFrame>
      <p className="text-foreground leading-relaxed">
        Use{" "}
        <span className="rounded bg-hue-violet-surface px-1 text-hue-violet-foreground">
          Frontend Design
        </span>{" "}
        to fix the flaky test in{" "}
        <span className="rounded bg-muted px-1 font-mono text-[0.92em]">surface.test.ts</span> and
        align the header with{" "}
        <span className="rounded bg-muted px-1 font-mono text-[0.92em]">SettingsPanels.tsx</span>{" "}
        before shipping.
      </p>
    </PreviewFrame>
  );
}

function CodePreview(): ReactNode {
  return (
    <PreviewFrame className="p-0">
      <pre className="overflow-x-auto px-3 py-2.5 font-mono text-[length:var(--forge-code-font-size,13px)] leading-relaxed">
        <code>
          <span className="block text-secondary-label">src/formatUser.ts</span>
          <span className="block bg-destructive-surface">
            {"- return user.name.toUpperCase();"}
          </span>
          <span className="block bg-success-surface">
            {"+ return `${user.name} <${user.email}>`;"}
          </span>
        </code>
      </pre>
    </PreviewFrame>
  );
}

function TerminalPreview(): ReactNode {
  return (
    <PreviewFrame className="p-0">
      <pre className="overflow-x-auto px-3 py-2.5 font-[family-name:var(--font-terminal,var(--font-mono))] text-[length:var(--forge-terminal-font-size,var(--forge-code-font-size,13px))] leading-relaxed">
        <code>
          <span className="block">
            <span className="text-hue-violet-foreground">VITE</span> ready in{" "}
            <span className="font-semibold">1.24s</span>
          </span>
          <span className="block text-secondary-label">{"→  Local:   http://127.0.0.1:5173/"}</span>
          <span className="block text-success-foreground">{"✓ 85 passed"}</span>
        </code>
      </pre>
    </PreviewFrame>
  );
}

function InterfaceFontRow(props: { readonly preview?: ReactNode }): ReactNode {
  const family = useSettings((settings) => settings.fontFamilySans);
  const size = useSettings((settings) => settings.uiFontSize);
  const { updateSettings } = useUpdateSettings();
  const isDefault =
    family === DEFAULT_UNIFIED_SETTINGS.fontFamilySans &&
    size === DEFAULT_UNIFIED_SETTINGS.uiFontSize;

  return (
    <SettingsRow
      description="Everything outside code blocks and the terminal. The size is the root the whole interface scales from."
      resetAction={
        isDefault ? null : (
          <SettingResetButton
            label="interface font"
            onClick={() => {
              updateSettings({
                fontFamilySans: DEFAULT_UNIFIED_SETTINGS.fontFamilySans,
                uiFontSize: DEFAULT_UNIFIED_SETTINGS.uiFontSize,
              });
            }}
          />
        )
      }
      {...searchableSetting("typography-interface-font")}
      control={
        <FontControls
          catalog={SANS_CATALOG}
          family={family}
          familyLabel="Interface font family"
          maximum={MAX_APP_FONT_SIZE}
          minimum={MIN_APP_FONT_SIZE}
          onFamilyChange={(fontFamilySans) => {
            updateSettings({ fontFamilySans });
          }}
          onSizeChange={(uiFontSize) => {
            updateSettings({ uiFontSize });
          }}
          size={size}
          sizeLabel="Interface font size"
        />
      }
    >
      <InterfacePreview />
      {props.preview}
    </SettingsRow>
  );
}

function CodeFontRow(props: {
  readonly title?: string;
  readonly description?: string;
  readonly preview?: ReactNode;
}): ReactNode {
  const family = useSettings((settings) => settings.fontFamilyCode);
  const size = useSettings((settings) => settings.codeFontSize);
  const { updateSettings } = useUpdateSettings();
  const isDefault =
    family === DEFAULT_UNIFIED_SETTINGS.fontFamilyCode &&
    size === DEFAULT_UNIFIED_SETTINGS.codeFontSize;

  return (
    <SettingsRow
      description={props.description ?? "Code blocks, diffs, and file previews."}
      resetAction={
        isDefault ? null : (
          <SettingResetButton
            label="monospace font"
            onClick={() => {
              updateSettings({
                codeFontSize: DEFAULT_UNIFIED_SETTINGS.codeFontSize,
                fontFamilyCode: DEFAULT_UNIFIED_SETTINGS.fontFamilyCode,
              });
            }}
          />
        )
      }
      // Bound to the catalog so a "Monospace font" result can actually scroll
      // to this row. `props.title` still wins when a caller renames it, but the
      // anchor id is unconditional -- the row mounts in both the simple and the
      // advanced layout, so the id is always present exactly once.
      {...searchableSetting("typography-monospace-font")}
      {...(props.title === undefined ? {} : { title: props.title })}
      control={
        <FontControls
          catalog={MONO_CATALOG}
          family={family}
          familyLabel="Monospace font family"
          maximum={MAX_APP_FONT_SIZE}
          minimum={MIN_APP_FONT_SIZE}
          onFamilyChange={(fontFamilyCode) => {
            updateSettings({ fontFamilyCode });
          }}
          onSizeChange={(codeFontSize) => {
            updateSettings({ codeFontSize });
          }}
          size={size}
          sizeLabel="Monospace font size"
        />
      }
    >
      {props.preview ?? <CodePreview />}
    </SettingsRow>
  );
}

function PromptFontRow(): ReactNode {
  const family = useSettings((settings) => settings.fontFamilyComposer);
  const size = useSettings((settings) => settings.promptFontSize);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      description="The composer only. Independent of the interface scale around it."
      {...searchableSetting("typography-prompt-font")}
      control={
        <FontControls
          catalog={SANS_CATALOG}
          family={family}
          familyLabel="Prompt font family"
          maximum={MAX_PROMPT_FONT_SIZE}
          minimum={MIN_PROMPT_FONT_SIZE}
          onFamilyChange={(fontFamilyComposer) => {
            updateSettings({ fontFamilyComposer });
          }}
          onSizeChange={(promptFontSize) => {
            updateSettings({ promptFontSize });
          }}
          size={size}
          sizeLabel="Prompt font size"
        />
      }
    />
  );
}

function TerminalFontRow(): ReactNode {
  const family = useSettings((settings) => settings.fontFamilyTerminal);
  const size = useSettings((settings) => settings.terminalFontSize);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      description="The terminal only. Its size decides how many columns fit before lines wrap, which is a different judgement from how large a diff should read."
      {...searchableSetting("typography-terminal-font")}
      control={
        <FontControls
          catalog={MONO_CATALOG}
          family={family}
          familyLabel="Terminal font family"
          maximum={MAX_TERMINAL_FONT_SIZE}
          minimum={MIN_TERMINAL_FONT_SIZE}
          onFamilyChange={(fontFamilyTerminal) => {
            updateSettings({ fontFamilyTerminal });
          }}
          onSizeChange={(terminalFontSize) => {
            updateSettings({ terminalFontSize });
          }}
          size={size}
          sizeLabel="Terminal font size"
        />
      }
    >
      <TerminalPreview />
    </SettingsRow>
  );
}

function FontSmoothingRow(): ReactNode {
  const fontSmoothing = useSettings((settings) => settings.fontSmoothing);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      description="Grayscale antialiasing — thinner stems. Off restores the platform default, which macOS renders heavier. Only Apple engines honour this."
      {...searchableSetting("typography-font-smoothing")}
      control={
        <Switch
          aria-label="Font smoothing"
          checked={fontSmoothing}
          onCheckedChange={(checked) => {
            updateSettings({ fontSmoothing: Boolean(checked) });
          }}
        />
      }
    />
  );
}

function WordWrapRow(): ReactNode {
  const diffWordWrap = useSettings((settings) => settings.diffWordWrap);
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsRow
      description="Wrap long lines in diffs and file previews by default."
      resetAction={
        diffWordWrap === DEFAULT_UNIFIED_SETTINGS.diffWordWrap ? null : (
          <SettingResetButton
            label="word wrapping"
            onClick={() => {
              updateSettings({ diffWordWrap: DEFAULT_UNIFIED_SETTINGS.diffWordWrap });
            }}
          />
        )
      }
      {...searchableSetting("typography-word-wrap")}
      control={
        <Switch
          aria-label="Wrap long lines in diffs and file previews"
          checked={diffWordWrap}
          onCheckedChange={(checked) => {
            updateSettings({ diffWordWrap: Boolean(checked) });
          }}
        />
      }
    />
  );
}

export function TypographySection(): ReactNode {
  const [advanced, setAdvanced] = useLocalStorage(
    TYPOGRAPHY_ADVANCED_STORAGE_KEY,
    false,
    Schema.Boolean,
  );
  const setAdvancedChecked = useCallback(
    (checked: boolean) => {
      setAdvanced(checked);
    },
    [setAdvanced],
  );

  return (
    <SettingsSection
      title="Typography"
      headerAction={
        <label className="flex cursor-pointer items-center gap-2 font-medium text-muted-foreground text-xs">
          Advanced
          <Switch
            aria-label="Show advanced typography settings"
            checked={advanced}
            onCheckedChange={(checked) => {
              setAdvancedChecked(Boolean(checked));
            }}
          />
        </label>
      }
    >
      {advanced ? (
        <>
          <InterfaceFontRow />
          <PromptFontRow />
          <CodeFontRow />
          <TerminalFontRow />
          <FontSmoothingRow />
        </>
      ) : (
        <>
          <InterfaceFontRow />
          <CodeFontRow
            description="Code blocks, diffs, file previews, and the terminal."
            preview={
              <>
                <CodePreview />
                <TerminalPreview />
              </>
            }
          />
        </>
      )}
      <WordWrapRow />
    </SettingsSection>
  );
}
